import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { FormikProps } from 'formik';
import { DateTime } from 'luxon';
import { MilanMuncipality } from 'practice/constants/muncipality';
import { TypeRegister } from 'practice/constants/type-register';
import { Fragment, memo, useEffect, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { PracticeAddress } from 'shell/models/practice-address';
import Form from './form';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import {
  RegisterOfficeVM,
  ResidenceTemporaryVM,
  ItalianVM,
} from 'shell/view-models/register-office-practice-vm';

interface AccordionResidenceProps {
  disabled?: boolean;
  ForPracticeTemporary?: boolean;
  formContext: FormikProps<RegisterOfficeVM>;
  codeEnte?: string;
  yearId?: string;
  typeRegister?: string;
  progressive?: string;
}

const AccordionResidenceComponent = ({
  disabled = false,
  formContext,
  codeEnte,
  yearId,
  typeRegister,
  progressive,
}: AccordionResidenceProps): React.ReactElement => {
  const [open, setOpen] = useState(true);

  const [indirizzo] = useHttpFetch<HttpResponse<PracticeAddress>, PracticeAddress>(
    `${__PRACTICE_API_URL__}/pratiche/indirizzi/italiano-o-estero/${codeEnte}-${yearId}-${typeRegister}-${progressive}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: (response) => {
        return response.data.payload;
      },
    }
  );

  useEffect(() => {
    if (indirizzo.isLoading || indirizzo.hasErrors || indirizzo.pristine || !indirizzo.data) {
      return;
    }

    const data: PracticeAddress = (indirizzo.data as PracticeAddress) || {};
    const dataItalian = data?.indirizzoVO || undefined;

    const residenceTemporary: ResidenceTemporaryVM = {
      isSaved: Boolean(dataItalian?.citta),
      effectiveDate: data.dataDecorrenza ? DateTime.fromISO(data.dataDecorrenza || '') : undefined,
      italian: dataItalian
        ? ({
            municipality: {
              // id:
              description:
                dataItalian?.citta || typeRegister === TypeRegister.RegisterOfficeTemporary
                  ? MilanMuncipality.description
                  : '',
              province:
                dataItalian?.provincia || typeRegister === TypeRegister.RegisterOfficeTemporary
                  ? MilanMuncipality.province
                  : '',
              provinceCode:
                dataItalian?.provincia || typeRegister === TypeRegister.RegisterOfficeTemporary
                  ? MilanMuncipality.provinceCode
                  : '',
            },
            cap: dataItalian?.cap || '',
            address: dataItalian?.nomeVia
              ? {
                  code: dataItalian.codVia,
                  description: dataItalian.nomeVia,
                }
              : null,
            number: dataItalian.numeroCivico || '',
            exponent: dataItalian.esponente || '',
            // letter: dataItalian.lettera || '',
            section: dataItalian.sezione || '',
            sheet: dataItalian.foglio || '',
            particle: dataItalian.particellaMappale || '',
            sub: dataItalian.subalterno || '',
            cat: dataItalian.categoriaCatastale || '',
            isHomeless: dataItalian.flagIgnotaDimora === '1',
            codTipoVia: dataItalian.codTipoVia || '',
            addressAuto: dataItalian.codVia
              ? {
                  code: dataItalian.codVia,
                  description: dataItalian.nomeVia,
                }
              : undefined,
            descrizioneSpecieToponimo: dataItalian.descrizioneSpecieToponimo || '',
            houseOccupationType: dataItalian.tipoOccupazione || '',
            officeContract: dataItalian.sedeAgenziaEntrate
              ? {
                  id: dataItalian.sedeAgenziaEntrate,
                  value: dataItalian.sedeAgenziaEntrate,
                }
              : null,
            dateContract: dataItalian.dataRegistrazioneAgenziaEntrate
              ? DateTime.fromISO(dataItalian.dataRegistrazioneAgenziaEntrate)
              : null,
            numberContract: dataItalian.numeroRegistrazioneAgenziaEntrate?.toString() || '',
            note: dataItalian.notaOperatore || '',
          } as ItalianVM)
        : TypeRegister.RegisterOfficeTemporary === typeRegister
        ? {
            municipality: MilanMuncipality,
          }
        : undefined,
    };

    formContext.setFieldValue('residenceTemporary', residenceTemporary);
  }, [indirizzo]);

  const updateData = useHttpClient(
    `${__PRACTICE_API_URL__}/pratiche/registrazione-nuovo-indirizzo`,
    {
      options: { method: 'POST' },
    }
  );

  const onSave = useEventCallback(() => {
    const data = {
      casoPratica: '1',
      dataDecorrenza: formContext.values.residenceTemporary?.effectiveDate
        ? formContext.values.residenceTemporary?.effectiveDate.toFormat('yyyy-MM-dd')
        : undefined,
      pratica: {
        key: {
          codiceEnte: codeEnte || undefined,
          tipoRegistro: typeRegister || undefined,
          idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
          progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
        },
      },
      indirizzo: {
        codVia:
          formContext.values.residenceTemporary?.italian?.address?.code ||
          formContext.values.residenceTemporary?.italian?.addressAuto?.code ||
          undefined,
        citta:
          formContext.values.residenceTemporary?.italian?.municipality?.description || undefined,
        provincia:
          formContext.values.residenceTemporary?.italian?.municipality?.province || undefined,
        cap: formContext.values.residenceTemporary?.italian?.cap || undefined,
        nomeVia:
          formContext.values.residenceTemporary?.italian?.address?.description ||
          formContext.values.residenceTemporary?.italian?.addressAuto?.description ||
          undefined,
        numeroCivico: formContext.values.residenceTemporary?.italian?.number || undefined,
        esponente: formContext.values.residenceTemporary?.italian?.exponent || undefined,
        lettera: formContext.values.residenceTemporary?.italian?.letter || undefined,
        sezione: formContext.values.residenceTemporary?.italian?.section || undefined,
        foglio: formContext.values.residenceTemporary?.italian?.sheet || undefined,
        particellaMappale: formContext.values.residenceTemporary?.italian?.particle || undefined,
        subalterno: formContext.values.residenceTemporary?.italian?.sub || undefined,
        categoriaCatastale: formContext.values.residenceTemporary?.italian?.cat || undefined,
        flagIgnotaDimora: formContext.values.residenceTemporary?.italian?.isHomeless ? '1' : '0',
        descrizioneSpecieToponimo:
          formContext.values.residenceTemporary?.italian?.descrizioneSpecieToponimo || undefined,
        codTipoVia: formContext.values.residenceTemporary?.italian?.codTipoVia || undefined,
        tipoOccupazione:
          formContext.values.residenceTemporary?.italian?.houseOccupationType || undefined,
        sedeAgenziaEntrate:
          formContext.values.residenceTemporary?.italian?.officeContract || undefined,
        dataRegistrazioneAgenziaEntrate:
          formContext.values.residenceTemporary?.italian?.dateContract?.toFormat('yyyy-MM-dd') ||
          undefined,
        numeroRegistrazioneAgenziaEntrate:
          formContext.values.residenceTemporary?.italian?.numberContract || undefined,
        notaOperatore: formContext.values.residenceTemporary?.italian?.note || undefined,
      },
    };

    (updateData({ data }) as ResponsePromise<void>).then(() => {
      toast.success('Operazione eseguita con successo');

      formContext.setFieldValue('residenceTemporary', {
        ...formContext.values.residenceTemporary,
        isSaved: true,
      });
    });
  });

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
      >
        <AccordionSummary
          aria-controls="panel-new-residence-content"
          id="panel-new-residence-header"
          expandIcon={<ExpandMore />}
        >
          <Typography>Residenza Temporanea</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Form disabled={disabled} formContext={formContext} onSave={onSave} />
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

AccordionResidenceComponent.displayName = 'Accordion Residence';

const AccordionResidence = memo(AccordionResidenceComponent);

export default AccordionResidence;
